import { Colors, Lightning } from '@lightningjs/sdk';
import Tile, { TileTemplateSpec } from 'components/common/tiles/Tile';
import Badge from 'components/common/Badge';
import { translate } from 'support/translate';
import { Video } from 'types/api/media';
import {
  getExpiringDateString,
  getSeasonEpisodeFormatted,
  isMediaExpiring,
} from 'support/contentUtils';
import { getFontFaceFromStyle } from 'support/textUtils';
import { SelectItemContext } from 'types/events';
import { getImageTextureObj } from 'support/generalUtils';
import { constants } from 'aliases';
import TextLabel from '../TextLabel';

export interface VideoTileTemplateSpec extends TileTemplateSpec {
  data: Video;

  Gradient: Lightning.Component;
  Badge: typeof Badge;
  Title: typeof TextLabel;
  Description: typeof TextLabel;
  PlayButton: Lightning.Texture;
}

const TILE_WIDTH = 400;
const TILE_HEIGHT = 225;

const BADGE_PADDING = 5;
const TEXT_PADDING_X = 10;
const TITLE_OFFSET_Y = 39;
const TITLE_MAX_LINES = 1;
const TITLE_FONT_SIZE = 26;
const DESCRIPTION_OFFSET_Y = 10;
const DESCRIPTION_FONT_SIZE = 22;
const DESCRIPTION_MAX_LINES = 1;

const PLAY_BUTTON_WIDTH = 34;
const PLAY_BUTTON_HEIGHT = 43;

export default class VideoTile extends Tile<VideoTileTemplateSpec> {
  protected _Badge = this.getByRef('Badge')!;
  protected _Title = this.getByRef('Title')!;
  protected _Description = this.getByRef('Description')!;
  protected _PlayButton = this.getByRef('PlayButton')!;

  isNew = false;
  isExpiring = false;

  override get title() {
    const title = this._Title.text?.text ?? '';
    const description = this._Description.text?.text ?? '';
    const badge = this._Badge.text?.text ?? '';

    return [title, description, badge];
  }

  override set data(data: Video) {
    super.data = data;

    const isNew = data.isNew;
    const expiring = isMediaExpiring(data);

    if (isNew) {
      this._Badge.label = translate('episode.new');
    } else if (expiring) {
      this._Badge.label = getExpiringDateString(data);
    }

    this.updateTitle(data);
    this.updateDescription(data);
  }

  override get action(): SelectItemContext['action'] {
    return 'play';
  }

  // static getters required for collection wrapper (List, Carousel, etc.)
  static override get width() {
    return TILE_WIDTH;
  }

  static override get height() {
    return TILE_HEIGHT;
  }

  static override get highlightWidth() {
    return TILE_WIDTH;
  }

  static override get highlightHeight() {
    return TILE_HEIGHT;
  }

  static override _template(): Lightning.Component.Template<VideoTileTemplateSpec> {
    const playButtonTexture = getImageTextureObj(
      'static/images/playback/player-play-icon.svg',
      PLAY_BUTTON_WIDTH,
      PLAY_BUTTON_HEIGHT,
    );

    return {
      ...super._template(),
      Gradient: {
        rect: true,
        zIndex: constants.ui.zIndex.floatingLayer - 1,
        w: w => w,
        h: h => h,
        colorTop: Colors('transparent').get(),
        colorBottom: Colors('background').get(),
      },
      Badge: {
        zIndex: constants.ui.zIndex.floatingLayer,
        type: Badge,
        mountX: 1,
        x: w => w - BADGE_PADDING,
        y: BADGE_PADDING,
      },
      Title: {
        type: TextLabel,
        zIndex: constants.ui.zIndex.floatingLayer,
        mountY: 1,
        x: TEXT_PADDING_X,
        y: h => h - TITLE_OFFSET_Y,
        w: w => w - TEXT_PADDING_X * 2,
        text: {
          fontSize: TITLE_FONT_SIZE,
          fontFace: getFontFaceFromStyle('medium'),
          maxLines: TITLE_MAX_LINES,
        },
      },
      Description: {
        type: TextLabel,
        zIndex: constants.ui.zIndex.floatingLayer,
        mountY: 1,
        x: TEXT_PADDING_X,
        y: h => h - DESCRIPTION_OFFSET_Y,
        w: w => w - TEXT_PADDING_X * 2,
        text: {
          fontSize: DESCRIPTION_FONT_SIZE,
          maxLines: DESCRIPTION_MAX_LINES,
        },
      },
      PlayButton: {
        alpha: 0,
        mount: 0.5,
        x: (w: number) => w / 2,
        y: (h: number) => h / 2,
        ...playButtonTexture,
      },
    };
  }

  override _focus() {
    this.updatePlayButton();
  }

  override _unfocus() {
    this.updatePlayButton();
  }

  protected updateTitle(video: Video) {
    const title = video.seriesName;
    this._Title.patch({ text: title });
  }

  protected updateDescription(video: Video) {
    const description = getSeasonEpisodeFormatted(video);
    this._Description.patch({ text: description });
  }

  private updatePlayButton() {
    const playButtonAlpha = this.hasFocus() ? 0.6 : 0;
    this._PlayButton.setSmooth('alpha', playButtonAlpha);
  }
}
